import React, { useState, useEffect } from 'react';
import { UserStatus, UserStatusInfo } from './NOCDashboard';
import { NOC_API_ENDPOINTS, nocApiRequest } from '../config/api';

interface NOCUserManagementProps {
  selectedUserId?: string | null;
}

const NOCUserManagement: React.FC<NOCUserManagementProps> = ({ selectedUserId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserStatusInfo | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<{action: string; userId: string; reason?: string} | null>(null);
  const [users, setUsers] = useState<UserStatusInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch unique users from audit events
  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Get recent events to extract unique user IDs
      const eventsData = await nocApiRequest(`${NOC_API_ENDPOINTS.events}?limit=100`);

      // Extract unique user IDs and aggregate data
      const userMap = new Map<string, UserStatusInfo>();

      if (eventsData.events) {
        for (const event of eventsData.events) {
          const userId = event.user_id;
          if (!userMap.has(userId)) {
            // Fetch user profile to get status
            try {
              const profileData = await nocApiRequest(NOC_API_ENDPOINTS.userProfile(userId));
              userMap.set(userId, {
                user_id: userId,
                status: profileData.user_status?.status || 'ACTIVE',
                blocked_at: profileData.user_status?.blocked_at,
                blocked_by: profileData.user_status?.blocked_by,
                blocked_reason: profileData.user_status?.status_reason,
                alert_count: profileData.recent_alerts?.length || 0,
                last_alert_at: profileData.recent_alerts?.[0]?.timestamp,
                request_count_24h: profileData.user_status?.request_count_24h || 0
              });
            } catch (error) {
              // If no profile exists, create basic entry
              userMap.set(userId, {
                user_id: userId,
                status: 'ACTIVE',
                alert_count: 0,
                request_count_24h: 0
              });
            }
          }
        }
      }

      setUsers(Array.from(userMap.values()));
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // Refresh every 15 seconds
    const interval = setInterval(fetchUsers, 15000);
    return () => clearInterval(interval);
  }, []);

  // Auto-select user when selectedUserId changes
  useEffect(() => {
    if (selectedUserId && users.length > 0) {
      const user = users.find(u => u.user_id === selectedUserId);
      if (user) {
        setSelectedUser(user);
        setSearchQuery(selectedUserId); // Also set search query to highlight the user
      }
    }
  }, [selectedUserId, users]);

  const filteredUsers = searchQuery
    ? users.filter(user => user.user_id.toLowerCase().includes(searchQuery.toLowerCase()))
    : users;

  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-900 text-green-300 border-green-700';
      case 'WARNING': return 'bg-yellow-900 text-yellow-300 border-yellow-700';
      case 'THROTTLED': return 'bg-orange-900 text-orange-300 border-orange-700';
      case 'BLOCKED': return 'bg-red-900 text-red-300 border-red-700';
    }
  };

  const getStatusIcon = (status: UserStatus) => {
    switch (status) {
      case 'ACTIVE':
        return <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />;
      case 'WARNING':
        return <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />;
      case 'THROTTLED':
        return <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />;
      case 'BLOCKED':
        return <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />;
    }
  };

  const handleBlockUser = async (userId: string, reason?: string) => {
    try {
      await nocApiRequest(NOC_API_ENDPOINTS.blockUser(userId), {
        method: 'PUT',
        body: JSON.stringify({
          reason: reason || showConfirmation?.reason || 'Blocked by NOC operator',
          operator: 'operator_demo'
        })
      });

      console.log(`User ${userId} blocked successfully`);
      setShowConfirmation(null);
      setSelectedUser(null);

      // Refresh the user list
      await fetchUsers();
    } catch (error) {
      console.error('Failed to block user:', error);
      window.alert('Failed to block user. Please try again.');
    }
  };

  const handleUnblockUser = async (userId: string) => {
    try {
      await nocApiRequest(NOC_API_ENDPOINTS.unblockUser(userId), {
        method: 'PUT',
        body: JSON.stringify({
          operator: 'operator_demo'
        })
      });

      console.log(`User ${userId} unblocked successfully`);
      setShowConfirmation(null);
      setSelectedUser(null);

      // Refresh the user list
      await fetchUsers();
    } catch (error) {
      console.error('Failed to unblock user:', error);
      window.alert('Failed to unblock user. Please try again.');
    }
  };

  const handleThrottleUser = async (userId: string) => {
    // Throttling is implemented as a temporary block with a duration
    try {
      await nocApiRequest(NOC_API_ENDPOINTS.blockUser(userId), {
        method: 'PUT',
        body: JSON.stringify({
          reason: 'Temporarily throttled due to suspicious activity',
          operator: 'operator_demo',
          duration: 1 // 1 hour auto-unblock
        })
      });

      console.log(`User ${userId} throttled successfully`);
      setShowConfirmation(null);
      setSelectedUser(null);

      // Refresh the user list
      await fetchUsers();
    } catch (error) {
      console.error('Failed to throttle user:', error);
      window.alert('Failed to throttle user. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-400 mb-2">Search User</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter user ID, session ID, or IP address..."
                className="w-full px-4 py-2 pl-10 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-750 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h3 className="text-sm font-semibold text-white">User Status</h3>
              {selectedUserId && (
                <span className="px-2 py-0.5 text-xs bg-blue-900 text-blue-300 rounded border border-blue-700">
                  Viewing: {selectedUserId}
                </span>
              )}
            </div>
            <span className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded">
              {filteredUsers.length} users
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-sm">Loading users...</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
          {filteredUsers.map((user) => (
            <div
              key={user.user_id}
              className="px-4 py-4 hover:bg-gray-750 transition-colors cursor-pointer"
              onClick={() => setSelectedUser(user)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    <svg className={`w-8 h-8 ${user.status === 'ACTIVE' ? 'text-green-500' : user.status === 'WARNING' ? 'text-yellow-500' : user.status === 'THROTTLED' ? 'text-orange-500' : 'text-red-500'}`} fill="currentColor" viewBox="0 0 20 20">
                      {getStatusIcon(user.status)}
                    </svg>
                  </div>

                  {/* User Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <span className="text-sm font-mono text-white">{user.user_id}</span>
                      <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(user.status)}`}>
                        {user.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-400">
                      <span>{user.request_count_24h} requests (24h)</span>
                      {user.alert_count > 0 && (
                        <span className="text-yellow-500">
                          {user.alert_count} alert{user.alert_count > 1 ? 's' : ''}
                        </span>
                      )}
                      {user.last_alert_at && (
                        <span>Last alert: {new Date(user.last_alert_at).toLocaleTimeString()}</span>
                      )}
                    </div>
                    {user.blocked_at && (
                      <div className="mt-2 text-xs text-red-400">
                        🚫 Blocked: {user.blocked_reason}
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex items-center space-x-2">
                  {user.status === 'BLOCKED' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowConfirmation({action: 'unblock', userId: user.user_id});
                      }}
                      className="px-3 py-1.5 text-xs bg-green-900 text-green-300 rounded hover:bg-green-800 transition-colors"
                    >
                      Unblock
                    </button>
                  ) : (
                    <>
                      {user.status !== 'THROTTLED' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowConfirmation({action: 'throttle', userId: user.user_id});
                          }}
                          className="px-3 py-1.5 text-xs bg-orange-900 text-orange-300 rounded hover:bg-orange-800 transition-colors"
                        >
                          Throttle
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowConfirmation({action: 'block', userId: user.user_id});
                        }}
                        className="px-3 py-1.5 text-xs bg-red-900 text-red-300 rounded hover:bg-red-800 transition-colors"
                      >
                        Block
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-white">User Profile</h2>
                <span className={`px-3 py-1 text-sm rounded border ${getStatusColor(selectedUser.status)}`}>
                  {selectedUser.status}
                </span>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="text-xs text-gray-400">User ID</label>
                <p className="text-sm text-white font-mono">{selectedUser.user_id}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400">24h Request Count</label>
                  <p className="text-sm text-white">{selectedUser.request_count_24h}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-400">Alert Count</label>
                  <p className="text-sm text-white">{selectedUser.alert_count}</p>
                </div>
              </div>

              {selectedUser.last_alert_at && (
                <div>
                  <label className="text-xs text-gray-400">Last Alert</label>
                  <p className="text-sm text-white">{new Date(selectedUser.last_alert_at).toLocaleString()}</p>
                </div>
              )}

              {selectedUser.blocked_at && (
                <div className="bg-red-900/30 border border-red-700 rounded p-4">
                  <h4 className="text-sm font-semibold text-red-300 mb-2">Block Details</h4>
                  <div className="space-y-2 text-sm text-red-200">
                    <p><strong>Blocked At:</strong> {new Date(selectedUser.blocked_at).toLocaleString()}</p>
                    <p><strong>Blocked By:</strong> {selectedUser.blocked_by}</p>
                    <p><strong>Reason:</strong> {selectedUser.blocked_reason}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex justify-end space-x-3">
              {selectedUser.status === 'BLOCKED' ? (
                <button
                  onClick={() => {
                    setShowConfirmation({action: 'unblock', userId: selectedUser.user_id});
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 bg-green-900 text-green-300 rounded hover:bg-green-800 transition-colors"
                >
                  Unblock User
                </button>
              ) : (
                <>
                  {selectedUser.status !== 'THROTTLED' && (
                    <button
                      onClick={() => {
                        setShowConfirmation({action: 'throttle', userId: selectedUser.user_id});
                        setSelectedUser(null);
                      }}
                      className="px-4 py-2 bg-orange-900 text-orange-300 rounded hover:bg-orange-800 transition-colors"
                    >
                      Throttle User
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowConfirmation({action: 'block', userId: selectedUser.user_id});
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 bg-red-900 text-red-300 rounded hover:bg-red-800 transition-colors"
                  >
                    Block User
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Confirm Action</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-300">
                Are you sure you want to <strong className="text-white">{showConfirmation.action}</strong> user{' '}
                <strong className="text-white font-mono">{showConfirmation.userId}</strong>?
              </p>
              {showConfirmation.action === 'block' && (
                <p className="mt-2 text-sm text-red-400">
                  ⚠️ This will immediately prevent the user from accessing the system.
                </p>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-750 border-t border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(null)}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (showConfirmation.action === 'block') {
                    handleBlockUser(showConfirmation.userId);
                  } else if (showConfirmation.action === 'unblock') {
                    handleUnblockUser(showConfirmation.userId);
                  } else if (showConfirmation.action === 'throttle') {
                    handleThrottleUser(showConfirmation.userId);
                  }
                }}
                className={`px-4 py-2 rounded transition-colors ${
                  showConfirmation.action === 'block'
                    ? 'bg-red-900 text-red-300 hover:bg-red-800'
                    : showConfirmation.action === 'unblock'
                    ? 'bg-green-900 text-green-300 hover:bg-green-800'
                    : 'bg-orange-900 text-orange-300 hover:bg-orange-800'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NOCUserManagement;
